import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '@/api';
import { useAuthStore } from '@/stores/auth';
import type { TimesheetWeekView, TimesheetStatus } from '@ultisim/shared';
import { format, addWeeks, subWeeks, startOfWeek, endOfWeek } from 'date-fns';

// Parse "YYYY-MM-DD" as a local Date (not UTC), so date display matches the calendar day
// regardless of the user's timezone.
function parseLocalDate(yyyyMmDd: string): Date {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  return new Date(y, m - 1, d);
}

interface CreatedTask {
  id: string;
  title: string;
  projectId: string;
}

export default function TimeEntryPage() {
  const { user } = useAuthStore();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekView, setWeekView] = useState<TimesheetWeekView | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Track edited hours: taskId -> date -> hours
  const [editedHours, setEditedHours] = useState<Record<string, Record<string, number>>>({});
  // Track newly added tasks per project: projectId -> taskId
  const [addedTasks, setAddedTasks] = useState<Record<string, string[]>>({});
  // Inline task creation
  const [creatingTaskFor, setCreatingTaskFor] = useState<string | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  // Tasks created inline this session (before reload)
  const [inlineTasks, setInlineTasks] = useState<CreatedTask[]>([]);
  // DevOps sync
  const [syncingProject, setSyncingProject] = useState<string | null>(null);
  // Which projects are visible in the grid (user builds their own list)
  const [visibleProjectIds, setVisibleProjectIds] = useState<Set<string>>(new Set());

  // Track in-flight loads so a slow response from a previous week doesn't overwrite
  // the data for the week the user just navigated to (the "stuck on loading" symptom).
  const loadSeqRef = useRef(0);

  const loadWeekData = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setIsLoading(true);
    setError('');
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const data = await api.get<TimesheetWeekView>('/timesheets/week', { date: dateStr });
      // Ignore stale responses if the user has navigated to another week in the meantime
      if (seq !== loadSeqRef.current) return;
      setWeekView(data);

      // Initialize edited hours from existing task entries
      const hours: Record<string, Record<string, number>> = {};
      for (const group of data.entryGroups) {
        for (const taskRow of group.tasks) {
          hours[taskRow.task.id] = { ...taskRow.dailyHours };
        }
      }
      setEditedHours(hours);

      // Reset added-task state on load. Pre-population is now opt-in via the
      // "Copy from last week" button (see copyFromLastWeek below).
      setAddedTasks({});

      // Auto-show projects that already have entries this week
      const autoShowProjects = new Set(
        data.entryGroups
          .filter(g => g.tasks.length > 0)
          .map(g => g.project.id)
      );
      setVisibleProjectIds(autoShowProjects);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      console.error('Failed to load week data:', err);
      const message = err instanceof Error ? err.message : 'Failed to load timesheet';
      setError(message);
    } finally {
      // Only clear loading if this is still the latest request
      if (seq === loadSeqRef.current) setIsLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    loadWeekData();
  }, [loadWeekData]);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  // Drafts and rejected timesheets are editable. Once submitted (or approved), they're locked.
  const isLocked = weekView?.status === 'submitted' || weekView?.status === 'approved';

  const handleHoursChange = (taskId: string, date: string, value: string) => {
    if (isLocked) return;
    const hours = Math.max(0, Math.min(24, parseFloat(value) || 0));
    setEditedHours((prev) => ({
      ...prev,
      [taskId]: {
        ...(prev[taskId] || {}),
        [date]: hours,
      },
    }));
  };

  const addTaskToProject = (projectId: string, taskId: string) => {
    if (!taskId) return;
    setAddedTasks((prev) => ({
      ...prev,
      [projectId]: [...(prev[projectId] || []), taskId],
    }));
    // Initialize empty hours for this task
    setEditedHours((prev) => ({
      ...prev,
      [taskId]: {},
    }));
  };

  const createTaskInline = async (projectId: string) => {
    if (!newTaskTitle.trim() || !user) return;
    setIsCreatingTask(true);
    try {
      const task = await api.post<{ id: string; title: string }>('/tasks', {
        title: newTaskTitle.trim(),
        projectId,
        userId: user.id,
      });
      // Track the created task and add it to the grid
      setInlineTasks((prev) => [...prev, { id: task.id, title: task.title, projectId }]);
      addTaskToProject(projectId, task.id);
      setCreatingTaskFor(null);
      setNewTaskTitle('');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create task';
      setError(message);
    } finally {
      setIsCreatingTask(false);
    }
  };

  // Copy tasks from last week into this week's grid (additive only — never
  // overwrites hours that are already on the grid).
  const copyFromLastWeek = () => {
    if (!weekView || isLocked) return;
    const prevTasks = weekView.previousWeekTasks || [];
    if (prevTasks.length === 0) {
      setError("No tasks to copy — you didn't log time last week.");
      return;
    }

    const assignedIds = new Set(weekView.entryGroups.map(g => g.project.id));
    // Tasks already represented this week (saved with hours)
    const tasksOnGrid = new Set<string>();
    for (const g of weekView.entryGroups) {
      for (const tr of g.tasks) tasksOnGrid.add(tr.task.id);
    }

    let added = 0;
    setAddedTasks(prev => {
      const next: Record<string, string[]> = { ...prev };
      const alreadyAdded = new Set<string>();
      for (const list of Object.values(next)) for (const id of list) alreadyAdded.add(id);

      for (const t of prevTasks) {
        if (!assignedIds.has(t.projectId)) continue;
        if (tasksOnGrid.has(t.taskId)) continue;   // already saved with hours
        if (alreadyAdded.has(t.taskId)) continue;  // already on the grid as added
        if (!next[t.projectId]) next[t.projectId] = [];
        next[t.projectId] = [...next[t.projectId], t.taskId];
        added++;
      }
      return next;
    });

    // Initialize empty hours buckets for newly added rows so the inputs render at 0
    setEditedHours(prev => {
      const next = { ...prev };
      for (const t of prevTasks) {
        if (!assignedIds.has(t.projectId)) continue;
        if (tasksOnGrid.has(t.taskId)) continue;
        if (!next[t.taskId]) next[t.taskId] = {};
      }
      return next;
    });

    // Make sure the relevant projects are visible
    setVisibleProjectIds(prev => {
      const next = new Set(prev);
      for (const t of prevTasks) {
        if (assignedIds.has(t.projectId)) next.add(t.projectId);
      }
      return next;
    });

    if (added > 0) {
      setSuccessMsg(`Copied ${added} task${added === 1 ? '' : 's'} from last week. Existing hours are unchanged — just fill in the new ones.`);
    } else {
      setSuccessMsg('All of last week\'s tasks are already on this timesheet.');
    }
  };

  // Remove a newly-added task row (not yet saved) — purely client-side
  const removeAddedTask = (projectId: string, taskId: string) => {
    setAddedTasks(prev => ({
      ...prev,
      [projectId]: (prev[projectId] || []).filter(id => id !== taskId),
    }));
    setEditedHours(prev => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  };

  // Remove an existing task row — deletes all entries for that task in the week
  const removeExistingTaskRow = async (taskId: string) => {
    if (!weekView) return;
    if (isLocked) return;
    const confirmed = window.confirm('Remove this task from the timesheet for this week? All logged hours for this task in this week will be deleted.');
    if (!confirmed) return;
    try {
      await api.delete(
        `/time-entries/task-week?taskId=${encodeURIComponent(taskId)}&weekStart=${weekView.weekStart}&weekEnd=${weekView.weekEnd}`
      );
      setSuccessMsg('Row removed');
      await loadWeekData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to remove row';
      setError(message);
    }
  };

  const syncFromDevOps = async (projectId: string) => {
    setSyncingProject(projectId);
    setError('');
    try {
      const result = await api.post<{ created: number; updated: number; total: number }>(`/devops-sync/${projectId}`);
      setSuccessMsg(`Synced ${result.total} work items from DevOps (${result.created} new, ${result.updated} updated)`);
      await loadWeekData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sync from DevOps';
      setError(message);
    } finally {
      setSyncingProject(null);
    }
  };

  const saveDraft = async (silent = false) => {
    if (!weekView) return;
    if (!silent) {
      setIsSaving(true);
      setError('');
    }
    const entries: Array<{ projectId: string; taskId: string; date: string; hours: number }> = [];

    // Collect entries from existing task rows.
    // Send ALL days (including 0h) so the backend can delete entries that were cleared.
    for (const group of weekView.entryGroups) {
      for (const taskRow of group.tasks) {
        const taskHours = editedHours[taskRow.task.id] || {};
        const originalHours = taskRow.dailyHours || {};
        for (const date of weekView.dates) {
          const h = taskHours[date] ?? 0;
          const had = (originalHours[date] ?? 0) > 0;
          // Send the entry if it has hours now, OR if it had hours before (to delete it).
          if (h > 0 || had) {
            entries.push({ projectId: group.project.id, taskId: taskRow.task.id, date, hours: h });
          }
        }
      }
    }

    // Collect entries from newly added tasks — only send hours > 0
    for (const group of weekView.entryGroups) {
      const added = addedTasks[group.project.id] || [];
      for (const taskId of added) {
        const taskHours = editedHours[taskId] || {};
        for (const date of weekView.dates) {
          const h = taskHours[date] ?? 0;
          if (h > 0) {
            entries.push({ projectId: group.project.id, taskId, date, hours: h });
          }
        }
      }
    }

    if (entries.length > 0) {
      await api.post('/time-entries/bulk', { entries });
    }
    await loadWeekData();
    if (!silent) {
      setSuccessMsg('Draft saved');
      setIsSaving(false);
    }
  };

  const submitTimesheet = async () => {
    if (!weekView) return;
    setIsSubmitting(true);
    setError('');
    try {
      // Save first (silent mode — errors will propagate here)
      await saveDraft(true);
      // Then submit
      await api.post(`/timesheets/${weekView.id}/submit`);
      setShowSubmitConfirm(false);
      setSuccessMsg('Timesheet submitted for approval');
      // Advance to next week
      setCurrentDate((d) => addWeeks(d, 1));
    } catch (err: unknown) {
      const apiErr = err as { details?: Record<string, string[]>; message?: string };
      const details = apiErr.details ? Object.values(apiErr.details).flat().join('; ') : '';
      const message = details || (err instanceof Error ? err.message : 'Failed to submit');
      setError(message);
    } finally {
      setIsSubmitting(false);
      setShowSubmitConfirm(false);
    }
  };

  const previousWeek = () => setCurrentDate((d) => subWeeks(d, 1));
  const nextWeek = () => setCurrentDate((d) => addWeeks(d, 1));
  const goToToday = () => setCurrentDate(new Date());

  const weekStartDate = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEndDate = endOfWeek(currentDate, { weekStartsOn: 1 });

  const getTotalForDate = (date: string) => {
    return Object.values(editedHours).reduce((sum, taskH) => sum + (taskH[date] || 0), 0);
  };

  const getWeekTotal = () => {
    if (!weekView) return 0;
    return weekView.dates.reduce((sum, date) => sum + getTotalForDate(date), 0);
  };

  const getTaskTotal = (taskId: string) => {
    if (!weekView) return 0;
    return weekView.dates.reduce((sum, date) => sum + (editedHours[taskId]?.[date] || 0), 0);
  };

  // Get available tasks for a project (excluding already-shown tasks)
  const getAvailableTasksForProject = (projectId: string) => {
    const group = weekView?.entryGroups.find(g => g.project.id === projectId);
    if (!group) return [];
    const existingTaskIds = new Set(group.tasks.map(t => t.task.id));
    const addedIds = new Set(addedTasks[projectId] || []);
    return group.availableTasks.filter(t => !existingTaskIds.has(t.id) && !addedIds.has(t.id));
  };

  const statusBanner = (status: TimesheetStatus) => {
    switch (status) {
      case 'draft':
        return (
          <div className="p-3 bg-dark-800 border border-dark-600 rounded-lg text-dark-300 text-sm">
            Draft timesheet &mdash; log hours against your tasks and submit when ready.
          </div>
        );
      case 'submitted':
        return (
          <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg text-blue-400 text-sm flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Submitted {weekView?.submittedAt ? format(new Date(weekView.submittedAt), 'MMM d') : ''} &mdash; awaiting admin approval.
          </div>
        );
      case 'approved':
        return (
          <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg text-green-400 text-sm flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Approved {weekView?.reviewedAt ? format(new Date(weekView.reviewedAt), 'MMM d') : ''}.
            {weekView?.reviewerNotes && <span className="ml-1 text-green-300">Note: {weekView.reviewerNotes}</span>}
          </div>
        );
      case 'rejected':
        return (
          <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Rejected &mdash; please correct and resubmit.
            </div>
            {weekView?.reviewerNotes && (
              <p className="mt-2 text-red-300 pl-7">"{weekView.reviewerNotes}"</p>
            )}
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Time Entry</h1>
          <p className="text-dark-400 mt-1">Log hours against your tasks</p>
        </div>
        {!isLocked && (
          <div className="flex gap-3 flex-wrap">
            {(weekView?.previousWeekTasks?.length ?? 0) > 0 && (
              <button
                onClick={copyFromLastWeek}
                className="btn-ghost"
                title="Add last week's tasks to this week (does not overwrite hours you've already entered)"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                </svg>
                Copy from last week
              </button>
            )}
            <button onClick={async () => { setError(''); setIsSaving(true); try { await saveDraft(true); setSuccessMsg('Draft saved'); } catch (err: any) { const details = err.details ? Object.values(err.details).flat().join('; ') : ''; setError(details || err.message || 'Failed to save'); } finally { setIsSaving(false); } }} disabled={isSaving} className="btn-ghost">
              {isSaving ? <><span className="spinner w-4 h-4" /> Saving...</> : 'Save Draft'}
            </button>
            <button onClick={() => setShowSubmitConfirm(true)} className="btn-primary">
              Submit for Approval
            </button>
          </div>
        )}
      </div>

      {/* Messages */}
      {successMsg && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
          {successMsg}
        </div>
      )}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Status banner */}
      {weekView && statusBanner(weekView.status as TimesheetStatus)}

      {/* Week navigation */}
      <div className="card">
        <div className="flex items-center justify-between">
          <button onClick={previousWeek} className="btn-ghost">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          <div className="text-center">
            <p className="text-lg font-semibold text-white">
              {format(weekStartDate, 'MMM d')} - {format(weekEndDate, 'MMM d, yyyy')}
            </p>
            <button onClick={goToToday} className="text-sm text-primary-400 hover:text-primary-300">
              Go to today
            </button>
          </div>
          <button onClick={nextWeek} className="btn-ghost">
            Next
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Task-based time entry grid */}
      {weekView && weekView.entryGroups.length > 0 ? (
        <div className="card overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-dark-400 uppercase tracking-wider bg-dark-800/50 rounded-tl-lg" style={{minWidth: '240px'}}>
                  Project / Task
                </th>
                {weekView.dates.map((date) => (
                  <th key={date} className="px-2 py-3 text-center text-xs font-medium text-dark-400 uppercase tracking-wider bg-dark-800/50">
                    <div>{format(parseLocalDate(date), 'EEE')}</div>
                    <div className="text-sm font-normal normal-case">{format(parseLocalDate(date), 'MMM d')}</div>
                  </th>
                ))}
                <th className="px-4 py-3 text-center text-xs font-medium text-dark-400 uppercase tracking-wider bg-dark-800/50 rounded-tr-lg">
                  Total
                </th>
              </tr>
            </thead>
              {weekView.entryGroups.filter(g => visibleProjectIds.has(g.project.id)).map((group) => {
                const allTaskIds = [
                  ...group.tasks.map(t => t.task.id),
                  ...(addedTasks[group.project.id] || []),
                ];
                const projectTotal = allTaskIds.reduce((sum, tid) => sum + getTaskTotal(tid), 0);
                const availTasks = getAvailableTasksForProject(group.project.id);

                return (
                  <tbody key={group.project.id}>
                    {/* Project header row */}
                    <tr className="bg-dark-800/30">
                      <td className="px-4 py-2" colSpan={weekView.dates.length + 2}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.project.color }} />
                            <span className="font-medium text-dark-200">{group.project.name}</span>
                            {group.project.devopsProject && (
                              <button
                                onClick={() => syncFromDevOps(group.project.id)}
                                disabled={syncingProject === group.project.id}
                                className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1"
                              >
                                {syncingProject === group.project.id ? (
                                  <><span className="spinner w-3 h-3" /> Syncing...</>
                                ) : (
                                  <>
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Sync from DevOps
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-dark-400">{projectTotal}h</span>
                            {!isLocked && projectTotal === 0 && (
                              <button
                                onClick={() => setVisibleProjectIds(prev => { const next = new Set(prev); next.delete(group.project.id); return next; })}
                                className="text-dark-600 hover:text-dark-300 transition-colors"
                                title={`Remove ${group.project.name} from view`}
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>

                    {/* Existing task rows */}
                    {group.tasks.map((taskRow) => (
                      <tr key={taskRow.task.id} className="group/row border-t border-dark-800/50">
                        <td className="px-4 py-2 pl-10">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-dark-300 flex-1">{taskRow.task.title}</span>
                            {!isLocked && (
                              <button
                                onClick={() => removeExistingTaskRow(taskRow.task.id)}
                                className="text-dark-600 hover:text-red-400 transition-colors opacity-0 group-hover/row:opacity-100"
                                title="Remove this task row (deletes all hours for this task this week)"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                        {weekView.dates.map((date) => (
                          <td key={date} className="px-2 py-2 text-center">
                            <input
                              type="number"
                              min="0"
                              max="24"
                              value={editedHours[taskRow.task.id]?.[date] || 0}
                              onChange={(e) => handleHoursChange(taskRow.task.id, date, e.target.value)}
                              disabled={isLocked}
                              className={`w-16 px-2 py-1.5 text-center bg-dark-800 border border-dark-600 rounded-lg text-dark-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                            />
                          </td>
                        ))}
                        <td className="px-4 py-2 text-center font-medium text-white text-sm">
                          {getTaskTotal(taskRow.task.id)}h
                        </td>
                      </tr>
                    ))}

                    {/* Newly added task rows (from existing available tasks or inline-created) */}
                    {(addedTasks[group.project.id] || []).map((taskId) => {
                      const taskInfo = group.availableTasks.find(t => t.id === taskId)
                        || inlineTasks.find(t => t.id === taskId)
                        || (weekView.previousWeekTasks?.find(t => t.taskId === taskId)
                          ? { id: taskId, title: weekView.previousWeekTasks!.find(t => t.taskId === taskId)!.taskTitle }
                          : undefined);
                      if (!taskInfo) return null;
                      return (
                        <tr key={taskId} className="group/row border-t border-dark-800/50">
                          <td className="px-4 py-2 pl-10">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-primary-400 flex-1">{taskInfo.title}</span>
                              {!isLocked && (
                                <button
                                  onClick={() => removeAddedTask(group.project.id, taskId)}
                                  className="text-dark-600 hover:text-red-400 transition-colors opacity-0 group-hover/row:opacity-100"
                                  title="Remove this task row"
                                >
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                          {weekView.dates.map((date) => (
                            <td key={date} className="px-2 py-2 text-center">
                              <input
                                type="number"
                                min="0"
                                max="24"
                                value={editedHours[taskId]?.[date] || 0}
                                onChange={(e) => handleHoursChange(taskId, date, e.target.value)}
                                className="w-16 px-2 py-1.5 text-center bg-dark-800 border border-dark-600 rounded-lg text-dark-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                              />
                            </td>
                          ))}
                          <td className="px-4 py-2 text-center font-medium text-white text-sm">
                            {getTaskTotal(taskId)}h
                          </td>
                        </tr>
                      );
                    })}

                    {/* Existing available tasks dropdown */}
                    {!isLocked && availTasks.length > 0 && (
                      <tr className="border-t border-dark-800/30">
                        <td className="px-4 py-2 pl-10" colSpan={weekView.dates.length + 2}>
                          <select
                            className="text-sm bg-transparent border-none text-primary-400 cursor-pointer focus:ring-0 p-0"
                            value=""
                            onChange={(e) => addTaskToProject(group.project.id, e.target.value)}
                          >
                            <option value="" className="bg-dark-900 text-dark-400">Add existing task...</option>
                            {(() => {
                              // Group tasks by sprint prefix (e.g. "[Sprint 0]")
                              const groups: Record<string, typeof availTasks> = {};
                              for (const t of availTasks) {
                                const match = t.title.match(/^\[([^\]]+)\]/);
                                const group = match ? match[1] : 'Other';
                                if (!groups[group]) groups[group] = [];
                                groups[group].push(t);
                              }
                              const groupNames = Object.keys(groups);
                              // If only one group or no bracketed groups, render flat
                              if (groupNames.length <= 1) {
                                return availTasks.map((t) => (
                                  <option key={t.id} value={t.id} className="bg-dark-900 text-dark-200">{t.title}</option>
                                ));
                              }
                              return groupNames.map(name => (
                                <optgroup key={name} label={name}>
                                  {groups[name].map(t => (
                                    <option key={t.id} value={t.id} className="bg-dark-900 text-dark-200">
                                      {t.title.replace(/^\[[^\]]+\]\s*/, '')}
                                    </option>
                                  ))}
                                </optgroup>
                              ));
                            })()}
                          </select>
                        </td>
                      </tr>
                    )}

                    {/* Inline task creation */}
                    {!isLocked && (
                      <tr className="border-t border-dark-800/30">
                        <td className="px-4 py-2 pl-10" colSpan={weekView.dates.length + 2}>
                          {creatingTaskFor === group.project.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={newTaskTitle}
                                onChange={(e) => setNewTaskTitle(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') createTaskInline(group.project.id);
                                  if (e.key === 'Escape') { setCreatingTaskFor(null); setNewTaskTitle(''); }
                                }}
                                placeholder="Task name..."
                                className="input text-sm py-1 px-2 flex-1 max-w-xs"
                                autoFocus
                                disabled={isCreatingTask}
                              />
                              <button
                                onClick={() => createTaskInline(group.project.id)}
                                disabled={isCreatingTask || !newTaskTitle.trim()}
                                className="btn-sm btn-primary py-1"
                              >
                                {isCreatingTask ? <span className="spinner w-3 h-3" /> : 'Add'}
                              </button>
                              <button
                                onClick={() => { setCreatingTaskFor(null); setNewTaskTitle(''); }}
                                className="btn-sm btn-ghost py-1"
                                disabled={isCreatingTask}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setCreatingTaskFor(group.project.id); setNewTaskTitle(''); }}
                              className="text-sm text-primary-400 hover:text-primary-300"
                            >
                              + Add task
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                );
              })}
            <tfoot>
              <tr className="border-t-2 border-dark-700">
                <td className="px-4 py-3 font-medium text-dark-200">Daily Total</td>
                {weekView.dates.map((date) => (
                  <td
                    key={date}
                    className={`px-2 py-3 text-center font-medium ${
                      getTotalForDate(date) >= 8 ? 'text-accent-400' :
                      getTotalForDate(date) > 0 ? 'text-amber-400' : 'text-dark-500'
                    }`}
                  >
                    {getTotalForDate(date)}h
                  </td>
                ))}
                <td className="px-4 py-3 text-center">
                  <span className="font-bold text-white">
                    {getWeekTotal()}h
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>

          {/* Add Project / Add Time Off / Add Meetings */}
          {!isLocked && (() => {
            const SPECIAL_PROJECT_IDS = ['00000000000000000000000000000003', '00000000000000000000000000000004'];
            const SPECIAL_LABELS: Record<string, string> = {
              '00000000000000000000000000000003': '+ Add Time Off',
              '00000000000000000000000000000004': '+ Add Meetings',
            };
            const hiddenProjects = weekView.entryGroups.filter(g => !visibleProjectIds.has(g.project.id) && !SPECIAL_PROJECT_IDS.includes(g.project.id));
            const hiddenSpecial = weekView.entryGroups.filter(g => SPECIAL_PROJECT_IDS.includes(g.project.id) && !visibleProjectIds.has(g.project.id));
            if (hiddenProjects.length === 0 && hiddenSpecial.length === 0) return null;
            return (
              <div className="mt-4 pt-4 border-t border-dark-700 flex items-center gap-3 flex-wrap">
                {hiddenProjects.length > 0 && (
                  <>
                    <span className="text-sm text-dark-400">Add project:</span>
                    <select
                      className="input text-sm py-1.5 px-3 w-auto"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          setVisibleProjectIds(prev => new Set([...prev, e.target.value]));
                        }
                      }}
                    >
                      <option value="" className="bg-dark-900 text-dark-400">Select a project...</option>
                      {hiddenProjects.map(g => (
                        <option key={g.project.id} value={g.project.id} className="bg-dark-900 text-dark-200">
                          {g.project.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                {hiddenSpecial.map(g => (
                  <button
                    key={g.project.id}
                    onClick={() => setVisibleProjectIds(prev => new Set([...prev, g.project.id]))}
                    className="btn-sm bg-dark-700 hover:bg-dark-600 text-dark-300 hover:text-white transition-colors px-4 py-1.5 rounded-lg text-sm"
                  >
                    {SPECIAL_LABELS[g.project.id] || `+ Add ${g.project.name}`}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      ) : (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-dark-800 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-dark-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No tasks available</h3>
          <p className="text-dark-400">
            Create tasks on the Tasks page first, then log hours against them here.
          </p>
        </div>
      )}

      {/* Week summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card text-center">
          <p className="text-dark-400 text-sm">Week Total</p>
          <p className="text-3xl font-bold text-white mt-1">{getWeekTotal()}h</p>
        </div>
        <div className="card text-center">
          <p className="text-dark-400 text-sm">Projects</p>
          <p className="text-3xl font-bold text-dark-400 mt-1">{visibleProjectIds.size}</p>
        </div>
        <div className="card text-center">
          <p className="text-dark-400 text-sm">Avg per Day</p>
          <p className="text-3xl font-bold text-primary-400 mt-1">
            {weekView ? (getWeekTotal() / 5).toFixed(1) : '0'}h
          </p>
        </div>
      </div>

      {/* Submit confirmation modal */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card max-w-md w-full animate-slide-down">
            <h3 className="text-lg font-medium text-white mb-2">Submit Timesheet</h3>
            <p className="text-dark-400 mb-4">
              Submit <strong className="text-white">{getWeekTotal()} hours</strong> for the week of{' '}
              {format(weekStartDate, 'MMM d')} - {format(weekEndDate, 'MMM d, yyyy')}?
            </p>
            <p className="text-dark-500 text-sm mb-6">
              Your timesheet will be locked for editing until reviewed by an admin. You'll be able to edit and resubmit if rejected.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowSubmitConfirm(false)} className="btn-ghost" disabled={isSubmitting}>
                Cancel
              </button>
              <button onClick={submitTimesheet} className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? <><span className="spinner w-4 h-4" /> Submitting...</> : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
